import { useCallback, useEffect } from 'react'

export type AppLocale = 'it' | 'en'

export interface TranslationStrings {
  projects: string
  statics: string
  motion: string
  undo: string
  redo: string
  importPhoto: string
  photoSettings: string
  structureStatus: string
  calculate: string
  showDof: string
  elementForces: string
  more: string
  gridAndSnap: string
  loadCases: string
  pdfReport: string
  exportMarkdown: string
  imageBackground: string
  select: string
  beam: string
  commonNode: string
  support: string
  load: string
  dimension: string
  align: string
  erase: string
  grid: string
  gridSnap: string
  orthoSnap: string
  beamSnap: string
  nodeSnap: string
  visibility: string
  fit: string
  diagnostics: string
  simulate: string
  cancelSimulation: string
  beamBar: string
  slider: string
  actuator: string
  trace: string
  linear: string
  rotary: string
  horizontal: string
  vertical: string
  close: string
  properties: string
  results: string
  noSelection: string
  projectSettings: string
  labels: string
  beamMeasures: string
  dimensions: string
  alignments: string
  midpoints: string
  forces: string
  reactions: string
  pin: string
  roller: string
  fixed: string
  inclination: string
  force: string
  moment: string
  distributed: string
  aligned: string
  gridSize: string
  noBackground: string
  showPhoto: string
  redefineScale: string
  brightness: string
  opacity: string
  status: string
  notCalculated: string
  runSimulation: string
  projectTitle: string
  description: string
  newProject: string
  openExample: string
  toolDetailSelect: string
  toolDetailNode: string
  toolDetailElementStart: string
  toolDetailElementEnd: string
  toolDetailSupport: string
  toolDetailLoad: string
  toolDetailDimension: string
  toolDetailAlign: string
  toolDetailErase: string
  validProject: string
  needsCorrection: string
  engineIdle: string
  engineLoading: string
  engineReady: string
  validating: string
  engineError: string
  selection: string
  selectObject: string
  objectsAtPoint: string
  objectsAvailable: string
  chooseObject: string
  none: string
  canvasReady: string
  node: string
  element: string
  degrees: string
  dimensionMM: string
  modelCheck: string
  staticClassification: string
  howToIntervene: string
  mechanisms: string
  selfStress: string
  verificationNumbers: string
  modelLimits: string
  mobility: string
  passiveDof: string
  controlledDof: string
  remainingDof: string
  assembleInitial: string
  resetMotion: string
  contrastLines: string
  moreActions: string
  flipVertically: string
  removeCalibration: string
  deletePhoto: string
  copy: string
  share: string
  combinations: string
}

export type TranslationKey = keyof TranslationStrings

export interface AppStrings {
  common: {
    appName: string
    baseEditorLoading: string
  }
  gallery: {
    eyebrow: string
    title: string
    settings: string
    importProject: string
    newProject: string
    projects: string
    emptyTitle: string
    emptyDetail: string
    fixtureTitle: string
    fixtureSubtitle: string
    openFixture: string
  }
}

const translations: Record<AppLocale, AppStrings> = {
  it: {
    common: {
      appName: 'ForceCAD',
      baseEditorLoading: 'Caricamento editor Base',
    },
    gallery: {
      eyebrow: 'ForceCAD',
      title: 'Progetti',
      settings: 'Impostazioni',
      importProject: 'Importa progetto',
      newProject: 'Nuovo progetto',
      projects: 'I tuoi progetti',
      emptyTitle: 'Nessun progetto salvato',
      emptyDetail: 'Crea un progetto oppure importa un file ForceCAD.',
      fixtureTitle: 'Telaio di esempio',
      fixtureSubtitle: 'Apri il progetto dimostrativo nel visualizzatore Free',
      openFixture: 'Apri esempio',
    },
  },
  en: {
    common: {
      appName: 'ForceCAD',
      baseEditorLoading: 'Loading Base editor',
    },
    gallery: {
      eyebrow: 'ForceCAD',
      title: 'Projects',
      settings: 'Settings',
      importProject: 'Import project',
      newProject: 'New project',
      projects: 'Your projects',
      emptyTitle: 'No saved projects',
      emptyDetail: 'Create a project or import a ForceCAD file.',
      fixtureTitle: 'Example frame',
      fixtureSubtitle: 'Open the demonstration project in the Free viewer',
      openFixture: 'Open example',
    },
  },
}

const uiTranslations: Record<AppLocale, TranslationStrings> = {
  it: {
    projects: 'Progetti',
    statics: 'Statics',
    motion: 'Motion',
    undo: 'Annulla',
    redo: 'Ripristina',
    importPhoto: 'Importa foto',
    photoSettings: 'Impostazioni foto',
    structureStatus: 'Stato struttura',
    calculate: 'Calcola',
    showDof: 'Mostra DOF nodi',
    elementForces: 'Forze elemento',
    more: 'Altri comandi',
    gridAndSnap: 'Griglia e snap',
    loadCases: 'Casi di carico',
    pdfReport: 'Report PDF',
    exportMarkdown: 'Esporta verifica Markdown',
    imageBackground: 'Immagine/Sfondo',
    select: 'Seleziona',
    beam: 'Trave',
    commonNode: 'Nodo comune',
    support: 'Vincolo',
    load: 'Carico',
    dimension: 'Quota',
    align: 'Allinea',
    erase: 'Elimina',
    grid: 'Griglia',
    gridSnap: 'Snap griglia',
    orthoSnap: 'Snap orto',
    beamSnap: 'Snap travi',
    nodeSnap: 'Snap nodi',
    visibility: 'Visibilità',
    fit: 'Adatta',
    diagnostics: 'Diagnostica',
    simulate: 'Simula',
    cancelSimulation: 'Annulla simulazione',
    beamBar: 'Trave/Asta',
    slider: 'Pattino',
    actuator: 'Attuatore',
    trace: 'Traccia',
    linear: 'Lineare',
    rotary: 'Rotativo',
    horizontal: 'Orizzontale',
    vertical: 'Verticale',
    close: 'Chiudi',
    properties: 'Proprietà',
    results: 'Risultati',
    noSelection: 'Nessuna selezione',
    projectSettings: 'Impostazioni progetto',
    labels: 'Etichette',
    beamMeasures: 'Misure travi',
    dimensions: 'Quote',
    alignments: 'Allineamenti',
    midpoints: 'Punti medi',
    forces: 'Forze',
    reactions: 'Reazioni',
    pin: 'Cerniera',
    roller: 'Carrello',
    fixed: 'Incastro',
    inclination: 'Inclinazione piano',
    force: 'Forza',
    moment: 'Momento',
    distributed: 'Distribuito',
    aligned: 'Allineata',
    gridSize: 'Dimensione griglia',
    noBackground: 'Nessuna immagine di sfondo',
    showPhoto: 'Mostra foto',
    redefineScale: 'Ridefinisci scala',
    brightness: 'Luminosità',
    opacity: 'Trasparenza',
    status: 'Stato',
    notCalculated: 'Non calcolato',
    runSimulation: 'Esegui Simula dalla sezione Motion.',
    projectTitle: 'Titolo progetto',
    description: 'Descrizione',
    newProject: 'Nuovo progetto',
    openExample: 'Apri esempio',
    toolDetailSelect: 'Seleziona e trascina nodi esistenti.',
    toolDetailNode: 'Crea nodi sul canvas con snap motore.',
    toolDetailElementStart: 'Tocca un nodo esistente o un punto del canvas.',
    toolDetailElementEnd: 'Tocca il punto finale della trave.',
    toolDetailSupport: 'Applica un vincolo al nodo selezionato.',
    toolDetailLoad: 'Applica un carico puntuale al nodo selezionato.',
    toolDetailDimension: 'Seleziona due nodi.',
    toolDetailAlign: 'Seleziona il nodo di riferimento.',
    toolDetailErase: 'Seleziona un oggetto.',
    validProject: 'Progetto valido',
    needsCorrection: 'Da correggere',
    engineIdle: 'Motore inattivo',
    engineLoading: 'Motore in caricamento',
    engineReady: 'Motore pronto',
    validating: 'Validazione in corso',
    engineError: 'Errore motore',
    selection: 'Selezione',
    selectObject: 'Seleziona oggetto',
    objectsAtPoint: 'Oggetti nel punto selezionato',
    objectsAvailable: 'oggetti disponibili',
    chooseObject: 'Scegli l’oggetto da modificare.',
    none: 'Nessuna',
    canvasReady: 'Canvas pronto',
    node: 'Nodo',
    element: 'Elemento',
    degrees: 'Gradi',
    dimensionMM: 'Quota mm',
    modelCheck: 'Controllo del modello',
    staticClassification: 'Classificazione statica',
    howToIntervene: 'Come intervenire',
    mechanisms: 'Cinematismi',
    selfStress: 'Autotensione',
    verificationNumbers: 'Numeri della verifica',
    modelLimits: 'Limiti del modello',
    mobility: 'Mobilità',
    passiveDof: 'DOF passivi',
    controlledDof: 'DOF controllati',
    remainingDof: 'DOF rimanenti',
    assembleInitial: 'Assembla configurazione iniziale',
    resetMotion: 'Reimposta solo Motion',
    contrastLines: 'Contrasto linee',
    moreActions: 'Altre azioni',
    flipVertically: 'Capovolgi verticalmente',
    removeCalibration: 'Rimuovi calibrazione',
    deletePhoto: 'Elimina foto',
    copy: 'Copia',
    share: 'Condividi',
    combinations: 'Combinazioni',
  },
  en: {
    projects: 'Projects',
    statics: 'Statics',
    motion: 'Motion',
    undo: 'Undo',
    redo: 'Redo',
    importPhoto: 'Import photo',
    photoSettings: 'Photo settings',
    structureStatus: 'Structure status',
    calculate: 'Calculate',
    showDof: 'Show node DOFs',
    elementForces: 'Element forces',
    more: 'More commands',
    gridAndSnap: 'Grid and snapping',
    loadCases: 'Load cases',
    pdfReport: 'PDF report',
    exportMarkdown: 'Export verification Markdown',
    imageBackground: 'Image/Background',
    select: 'Select',
    beam: 'Beam',
    commonNode: 'Common node',
    support: 'Support',
    load: 'Load',
    dimension: 'Dimension',
    align: 'Align',
    erase: 'Erase',
    grid: 'Grid',
    gridSnap: 'Grid snap',
    orthoSnap: 'Ortho snap',
    beamSnap: 'Beam snap',
    nodeSnap: 'Node snap',
    visibility: 'Visibility',
    fit: 'Fit',
    diagnostics: 'Diagnostics',
    simulate: 'Simulate',
    cancelSimulation: 'Cancel simulation',
    beamBar: 'Beam/Bar',
    slider: 'Slider',
    actuator: 'Actuator',
    trace: 'Trace',
    linear: 'Linear',
    rotary: 'Rotary',
    horizontal: 'Horizontal',
    vertical: 'Vertical',
    close: 'Close',
    properties: 'Properties',
    results: 'Results',
    noSelection: 'No selection',
    projectSettings: 'Project settings',
    labels: 'Labels',
    beamMeasures: 'Beam measures',
    dimensions: 'Dimensions',
    alignments: 'Alignments',
    midpoints: 'Midpoints',
    forces: 'Forces',
    reactions: 'Reactions',
    pin: 'Pin',
    roller: 'Roller',
    fixed: 'Fixed',
    inclination: 'Plane inclination',
    force: 'Force',
    moment: 'Moment',
    distributed: 'Distributed',
    aligned: 'Aligned',
    gridSize: 'Grid size',
    noBackground: 'No background image',
    showPhoto: 'Show photo',
    redefineScale: 'Redefine scale',
    brightness: 'Brightness',
    opacity: 'Opacity',
    status: 'Status',
    notCalculated: 'Not calculated',
    runSimulation: 'Run Simulate from the Motion section.',
    projectTitle: 'Project title',
    description: 'Description',
    newProject: 'New project',
    openExample: 'Open example',
    toolDetailSelect: 'Select and drag existing nodes.',
    toolDetailNode: 'Create nodes on the canvas with engine snapping.',
    toolDetailElementStart: 'Tap an existing node or a point on the canvas.',
    toolDetailElementEnd: 'Tap the beam end point.',
    toolDetailSupport: 'Apply a support to the selected node.',
    toolDetailLoad: 'Apply a point load to the selected node.',
    toolDetailDimension: 'Select two nodes.',
    toolDetailAlign: 'Select the reference node.',
    toolDetailErase: 'Select an object.',
    validProject: 'Valid project',
    needsCorrection: 'Needs correction',
    engineIdle: 'Engine idle',
    engineLoading: 'Loading engine',
    engineReady: 'Engine ready',
    validating: 'Validating',
    engineError: 'Engine error',
    selection: 'Selection',
    selectObject: 'Select object',
    objectsAtPoint: 'Objects at the selected point',
    objectsAvailable: 'objects available',
    chooseObject: 'Choose the object to edit.',
    none: 'None',
    canvasReady: 'Canvas ready',
    node: 'Node',
    element: 'Element',
    degrees: 'Degrees',
    dimensionMM: 'Dimension mm',
    modelCheck: 'Model check',
    staticClassification: 'Static classification',
    howToIntervene: 'How to intervene',
    mechanisms: 'Mechanisms',
    selfStress: 'Self-stress',
    verificationNumbers: 'Verification numbers',
    modelLimits: 'Model limits',
    mobility: 'Mobility',
    passiveDof: 'Passive DOFs',
    controlledDof: 'Controlled DOFs',
    remainingDof: 'Remaining DOFs',
    assembleInitial: 'Assemble initial configuration',
    resetMotion: 'Reset Motion only',
    contrastLines: 'Line contrast',
    moreActions: 'More actions',
    flipVertically: 'Flip vertically',
    removeCalibration: 'Remove calibration',
    deletePhoto: 'Delete photo',
    copy: 'Copy',
    share: 'Share',
    combinations: 'Combinations',
  },
}

export function localeFromEnvironment(
  search = typeof window === 'undefined' ? '' : window.location.search,
  browserLanguages = typeof navigator === 'undefined' ? [] : navigator.languages,
): AppLocale {
  const override = new URLSearchParams(search).get('lang')?.toLowerCase()
  if (override === 'it' || override === 'en') {
    return override
  }

  const preferredLanguage = browserLanguages[0] ?? (typeof navigator === 'undefined' ? '' : navigator.language)
  return preferredLanguage.toLowerCase().startsWith('it') ? 'it' : 'en'
}

export function stringsFor(locale: AppLocale): AppStrings {
  return translations[locale]
}

export function applyDocumentLocale(locale: AppLocale): void {
  document.documentElement.lang = locale
}

export function useI18n(): {
  locale: AppLocale
  t: (key: TranslationKey) => string
} {
  const locale = localeFromEnvironment()

  useEffect(() => {
    applyDocumentLocale(locale)
  }, [locale])

  const t = useCallback((key: TranslationKey) => uiTranslations[locale][key], [locale])
  return { locale, t }
}
